<template>
  <v-card flat>
    <v-col cols="12" class="px-0">
      <v-btn color="primary" icon @click="beforeLeave">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pandaVideoAffiliate') }}
      </span>
    </v-col>

    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12">
        <v-card flat>
          <span v-html="$t('message.pages.video.messageAffiliate')"> </span>
        </v-card>
      </v-col>

      <v-col cols="12" class="mt-0 mb-4">
        <v-row v-if="hasEditVideoPerm">
          <v-col cols="7">
            <v-switch
              class="mt-2"
              v-model="pandaBranding"
              :label="$t('message.pages.video.pandaLogoMessage')"
              @click="changePlayer"
              hide-details
            ></v-switch>
          </v-col>
          <v-col cols="12" class="pt-3 ml-1 pb-2">
            <v-text-field
              v-model="refCode"
              :disabled="!pandaBranding"
              :placeholder="$t('message.playerPage.affiliateCode')"
              outlined
              :rules="[rules.maxLength, rules.minLength]"
              @blur="changePlayer"
              color="primary"
              :label="$t('message.playerPage.affiliateCode')"
              dense
              hide-details
            >
            </v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.updateMarketing') }}
    </span>

    <v-row no-gutters>
      <v-col cols="12">
        <v-card-actions class="px-0 pt-4 pb-6" v-if="hasEditVideoPerm">
          <v-spacer></v-spacer>
          <v-btn
            class="mr-2 px-4"
            text
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUndoChanges"
          >
            {{ $t('message.pages.video.discardChanges') }}
          </v-btn>
          <v-btn
            class="px-6"
            color="primary"
            :disabled="loadingButtonSave"
            :loading="loadingButtonSave"
            @click="handleUpdateMarketing"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
        <unsaved-changes-modal />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import UnsavedChangesModal from '../../../components/UnsavedChangesModal.vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import i18n from '@/translations/lang'
import _ from 'lodash'

export default {
  name: 'VideoPlayerAffiliate',
  components: {
    UnsavedChangesModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
  },
  data: () => ({
    videoId: '',
    loadingButtonSave: false,
    profileId: null,
    profileConfig: {},
    initialConfig: {},
    pandaBranding: false,
    refCode: '',
    internalError: false,
    rules: {
      maxLength: (v) =>
        (v && v.length <= 30) ||
        v.length === 0 ||
        i18n.t('message.maximumLengthConfigs'),
      minLength: (v) =>
        (v && v.length >= 3) ||
        v.length === 0 ||
        i18n.t('message.minimumLength'),
    },
    withoutChanges: true,
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.pandaBranding = this.$store.state.video.profile.vsl?.pandaBranding
    this.refCode = this.$store.state.video.profile?.vsl.refCode
    this.initialConfig['pandaBranding'] = _.cloneDeep(this.pandaBranding)
    this.initialConfig['refCode'] = _.cloneDeep(this.refCode)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    '$store.state.video.profile': {
      handler() {
        this.withoutChanges = this.checkChanges()
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
      hasEditVideoPerm() {
        return (
          this.$store.state.user.userPermissions?.includes('put-video') ?? false
        )
      },
    }),
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',

      'createProfile',

      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleUpdateMarketing() {
      this.loadingButtonSave = true
      const controls = this.$store.state.video.profile.controls
      const colors = this.$store.state.video.profile.colors
      const vsl = this.$store.state.video.profile.vsl
      if (
        (this.refCode.length < 3 && this.refCode.length !== 0) ||
        this.refCode.length > 30
      ) {
        this.loadingButtonSave = false
        return
      }
      try {
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }
        await this.updateProfile([
          this.profileId,
          {
            controls: controls,
            colors: colors,
            vsl: vsl,
            pandaBranding: this.pandaBranding,
          },
        ])
        this.internalError
          ? ''
          : ((this.initialConfig.refCode = _.cloneDeep(this.refCode)),
            (this.initialConfig.pandaBranding = _.cloneDeep(
              this.pandaBranding
            )),
            this.handleSnackbar(this.$t('message.pages.video.changesSaved')),
            (this.withoutChanges = true))
        this.loadingButtonSave = false
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
    },
    async changePlayer() {
      this.profile.vsl['refCode'] = this.refCode
      this.profile.vsl['pandaBranding'] = this.pandaBranding
      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)
    },
    async handleUndoChanges() {
      this.refCode = _.cloneDeep(this.initialConfig.refCode)
      this.pandaBranding = _.cloneDeep(this.initialConfig.pandaBranding)
      this.changePlayer()
    },
    checkChanges() {
      let verification1 = _.isEqual(
        this.profile.vsl.refCode,
        this.initialConfig.refCode
      )
      let verification3 = _.isEqual(
        this.profile.vsl.pandaBranding,
        this.initialConfig.pandaBranding
      )
      return verification1 && verification3
    },
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
  },
}
</script>

<style></style>
